import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import config from '../config';

const CssContext = createContext();

export const useCss = () => useContext(CssContext);

// Geçerli sezonluk mod değerleri
const VALID_MODES = ['none', 'snow', 'halloween', 'valentine', 'luck', 'fireworks', 'ramadan', 'kurban', 'matchday'];

// Bonus kart tasarım modu — 'image' (varsayılan, görselli kart) veya
// 'text-only' (görselsiz, kategori rozeti + başlık + buton şeklinde kompakt satır).
const VALID_CARD_LAYOUT_MODES = ['image', 'text-only'];

export const CssProvider = ({ children }) => {
  const [customBgImage, setCustomBgImage] = useState(null);
  const [customBgEnabled, setCustomBgEnabled] = useState(false);
  const [customLogoImage, setCustomLogoImage] = useState(null);
  const [customLogoEnabled, setCustomLogoEnabled] = useState(false);
  const [customAvatarImage, setCustomAvatarImage] = useState(null);
  const [customAvatarEnabled, setCustomAvatarEnabled] = useState(false);
  const [seasonalMode, setSeasonalModeRaw] = useState('none');
  const [longLogoVisible, setLongLogoVisible] = useState(true);
  const [longLogoVariant, setLongLogoVariantRaw] = useState('black');
  const [cardLayoutMode, setCardLayoutModeRaw] = useState('text-only');
  const [cardBadgePosition, setCardBadgePositionRaw] = useState('left');
  // Banner carousel state
  const [bannerImages, setBannerImages] = useState([]);
  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [bonusCardGoldMode, setBonusCardGoldMode] = useState(true);
  const [tabGoldMode, setTabGoldMode] = useState(false);

  const setCardLayoutMode = useCallback((mode) => {
    setCardLayoutModeRaw(VALID_CARD_LAYOUT_MODES.includes(mode) ? mode : 'image');
  }, []);

  const setCardBadgePosition = useCallback((pos) => {
    setCardBadgePositionRaw(['left', 'right', 'none'].includes(pos) ? pos : 'left');
  }, []);

  const setLongLogoVariant = useCallback((variant) => {
    setLongLogoVariantRaw(variant === 'white' ? 'white' : 'black');
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--long-logo-display',
      longLogoVisible ? 'flex' : 'none'
    );
  }, [longLogoVisible]);

  // Geriye dönük uyumluluk: snowEnabled hala okunabilir
  const snowEnabled = seasonalMode === 'snow';

  const setSeasonalMode = useCallback((mode) => {
    if (VALID_MODES.includes(mode)) {
      setSeasonalModeRaw(mode);
    } else {
      setSeasonalModeRaw('none');
    }
  }, []);

  // Eski API: snowEnabled toggle (sadece 'snow' veya 'none' geçişi)
  const setSnowEnabled = useCallback((value) => {
    setSeasonalModeRaw(value ? 'snow' : 'none');
  }, []);

  // Banner görsel yönetimi
  const addBannerImage = useCallback((base64) => {
    setBannerImages((prev) => [...prev, base64]);
  }, []);

  const removeBannerImage = useCallback((index) => {
    setBannerImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearBannerImages = useCallback(() => {
    setBannerImages([]);
  }, []);

  const reorderBannerImages = useCallback((fromIndex, toIndex) => {
    setBannerImages((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  // CSS verilerini API'den al ve uygula
  const fetchAndApplyCss = useCallback(async () => {
    try {
      if (!window.rh || !window.rh.cr) {
        console.warn('Güvenlik sistemi yüklenmemiş, default CSS kullanılıyor');
        return;
      }

      const payload = JSON.stringify({
        process_id: "get-css",
        user_id: "",
      });

      const keys = await window.rh.cr(payload);

      const response = await fetch(
        `${config.apiBaseUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "hk": keys[0],
            "hh": keys[1]
          },
          body: payload,
        }
      );

      if (!response.ok) {
        console.warn('CSS API isteği başarısız, default CSS kullanılıyor');
        return;
      }

      const result = await response.json();

      let cssData = result;

      if (result.status && result.data) {
        cssData = result.data;
      } else if (result.status === false) {
        console.warn('CSS API hatası:', result.message || 'Bilinmeyen hata');
        return;
      }

      if (cssData && typeof cssData === 'object') {
        console.log('✅ CSS verileri API\'den alındı:', Object.keys(cssData).length, 'özellik');

        Object.entries(cssData).forEach(([cssVar, value]) => {
          if (cssVar.startsWith('--') && value) {
            document.documentElement.style.setProperty(cssVar, value);
          }
        });

        if (cssData.customBgImage) setCustomBgImage(cssData.customBgImage);
        if (cssData.customBgEnabled !== undefined) setCustomBgEnabled(!!cssData.customBgEnabled);
        if (cssData.customLogoImage) setCustomLogoImage(cssData.customLogoImage);
        if (cssData.customLogoEnabled !== undefined) setCustomLogoEnabled(!!cssData.customLogoEnabled);
        if (cssData.customAvatarImage) setCustomAvatarImage(cssData.customAvatarImage);
        if (cssData.customAvatarEnabled !== undefined) setCustomAvatarEnabled(!!cssData.customAvatarEnabled);
        if (cssData.longLogoVisible !== undefined) setLongLogoVisible(!!cssData.longLogoVisible);
        if (cssData.longLogoVariant === 'white' || cssData.longLogoVariant === 'black') {
          setLongLogoVariantRaw(cssData.longLogoVariant);
        }

        // Sezonluk mod: önce yeni alan, yoksa eski snowEnabled flag'ine bak
        if (cssData.seasonalMode && VALID_MODES.includes(cssData.seasonalMode)) {
          setSeasonalModeRaw(cssData.seasonalMode);
        } else if (cssData.snowEnabled !== undefined) {
          setSeasonalModeRaw(cssData.snowEnabled ? 'snow' : 'none');
        }

        // Bonus kart tasarım modu — backend tarafindan flat alan olarak gönderiliyor.
        // Geçersiz/eksikse 'image' (mevcut görselli tasarım) varsayılan kalır.
        if (cssData.cardLayoutMode && VALID_CARD_LAYOUT_MODES.includes(cssData.cardLayoutMode)) {
          setCardLayoutModeRaw(cssData.cardLayoutMode);
        }
        if (cssData.cardBadgePosition && ['left', 'right', 'none'].includes(cssData.cardBadgePosition)) {
          setCardBadgePositionRaw(cssData.cardBadgePosition);
        }

        // Banner carousel verileri
        if (Array.isArray(cssData.bannerImages)) {
          setBannerImages(cssData.bannerImages);
        }
        if (cssData.bannerEnabled !== undefined) {
          setBannerEnabled(!!cssData.bannerEnabled);
        }
        if (cssData['--bonus-card-gold-mode'] !== undefined) {
          setBonusCardGoldMode(cssData['--bonus-card-gold-mode'] === 'true' || cssData['--bonus-card-gold-mode'] === true);
        }
        if (cssData['--tab-gold-mode'] !== undefined) {
          setTabGoldMode(cssData['--tab-gold-mode'] === 'true' || cssData['--tab-gold-mode'] === true);
        }
      } else {
        console.warn('Geçersiz CSS verisi, default CSS kullanılıyor');
      }

    } catch (error) {
      console.error('CSS yükleme hatası:', error);
      console.warn('Default CSS kullanılıyor');
    }
  }, []);

  const updateCustomBg = useCallback((base64) => {
    setCustomBgImage(base64);
    setCustomBgEnabled(true);
  }, []);

  const removeCustomBg = useCallback(() => {
    setCustomBgImage(null);
    setCustomBgEnabled(false);
  }, []);

  const updateCustomLogo = useCallback((base64) => {
    setCustomLogoImage(base64);
    setCustomLogoEnabled(true);
  }, []);

  const removeCustomLogo = useCallback(() => {
    setCustomLogoImage(null);
    setCustomLogoEnabled(false);
  }, []);

  const updateCustomAvatar = useCallback((base64) => {
    setCustomAvatarImage(base64);
    setCustomAvatarEnabled(true);
  }, []);

  const removeCustomAvatar = useCallback(() => {
    setCustomAvatarImage(null);
    setCustomAvatarEnabled(false);
  }, []);

  useEffect(() => {
    fetchAndApplyCss();
  }, [fetchAndApplyCss]);

  return (
    <CssContext.Provider value={{
      fetchAndApplyCss,
      customBgImage,
      customBgEnabled,
      setCustomBgEnabled,
      updateCustomBg,
      removeCustomBg,
      customLogoImage,
      customLogoEnabled,
      setCustomLogoEnabled,
      updateCustomLogo,
      removeCustomLogo,
      customAvatarImage,
      customAvatarEnabled,
      setCustomAvatarEnabled,
      updateCustomAvatar,
      removeCustomAvatar,
      // Long footer logo görünürlüğü
      longLogoVisible,
      setLongLogoVisible,
      // Long footer logo varyantı (black / white)
      longLogoVariant,
      setLongLogoVariant,
      // Sezonluk mod
      seasonalMode,
      setSeasonalMode,
      // Bonus kart tasarım modu ('image' | 'text-only')
      cardLayoutMode,
      setCardLayoutMode,
      cardBadgePosition,
      setCardBadgePosition,
      // Banner carousel
      bannerImages,
      setBannerImages,
      bannerEnabled,
      setBannerEnabled,
      addBannerImage,
      removeBannerImage,
      clearBannerImages,
      reorderBannerImages,
      // Geriye dönük uyumluluk
      snowEnabled,
      setSnowEnabled,
      // Gold Modları
      bonusCardGoldMode,
      setBonusCardGoldMode,
      tabGoldMode,
      setTabGoldMode,
    }}>
      {children}
    </CssContext.Provider>
  );
};
